import React, { useEffect, useState } from 'react';
import { motion, useMotionValue } from 'framer-motion';

// --- Sub-Component 1: The Glitching Cipher Text ---
const CipherText = ({ text, className, delay = 0 }) => {
  const [display, setDisplay] = useState('');
  const chars = "!@#$%^&*()_+-=[]{}|;:,.<>?/~`";
  // FIX: Define the constant to be used in setInterval
  const INTERVAL_MS = 40;

  useEffect(() => {
    let iteration = 0;
    let interval = null;
    
    // Initial delay for staggered appearance
    const startAnimation = () => {
      interval = setInterval(() => {
        setDisplay(
          text
            .split("")
            .map((letter, i) => {
              // Ensure spaces remain spaces
              if (letter === ' ') return ' ';
              if (i < iteration) return text[i];
              return chars[Math.floor(Math.random() * chars.length)];
            })
            .join("")
        );

        if (iteration >= text.length) clearInterval(interval);
        iteration += 1 / 2;
      }, INTERVAL_MS); // Use the constant
    }

    const timer = setTimeout(startAnimation, delay);

    return () => {
        clearInterval(interval);
        clearTimeout(timer);
    };
    // FIX: Added 'chars' and 'INTERVAL_MS' to the dependency array
  }, [text, delay, chars, INTERVAL_MS]);

  return <span className={className}>{display}</span>;
};

// --- Sub-Component 2: Mouse Tracker & Inverted Cursor ---
const useInvertedCursor = () => {
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);
    
    useEffect(() => {
        const handleMouseMove = (e) => {
            mouseX.set(e.clientX);
            mouseY.set(e.clientY);
        };
        
        window.addEventListener('mousemove', handleMouseMove);
        return () => window.removeEventListener('mousemove', handleMouseMove);
    }, [mouseX, mouseY]);

    return { mouseX, mouseY };
};


// --- Main Monolith Component ---
const BrutalistMonolithInterface = () => {
    const { mouseX, mouseY } = useInvertedCursor();

    return (
        <div className="relative w-full min-h-[100vh] bg-black text-[#f0f0f0] font-mono cursor-none select-none overflow-hidden">
            
            {/* 0. Inverted Lens Cursor (Spotlight Effect) */}
            <motion.div
                style={{ 
                    x: mouseX, 
                    y: mouseY, 
                    translateX: '-50%', 
                    translateY: '-50%' 
                }}
                className="fixed top-0 left-0 w-80 h-80 rounded-full bg-white/5 pointer-events-none z-50 blur-3xl mix-blend-overlay hidden lg:block"
            />

            {/* 1. Monolithic Data Grid Background (Fixed and Subtle) */}
            <div className="fixed inset-0 z-0 opacity-10 pointer-events-none"
                style={{ 
                    backgroundImage: `linear-gradient(to right, #f0f0f0 1px, transparent 1px), 
                                        linear-gradient(to bottom, #f0f0f0 1px, transparent 1px)`,
                    backgroundSize: '30px 30px',
                }}
            />

            {/* 2. Fixed Vertical Data Bars (System Status) - LEFT SIDEBAR */}
            {/* FIX: Changed w-24 to w-12 on mobile, w-24 on md screens and up */}
<div className="fixed top-0 bottom-0 left-0 w-12 md:w-24 flex flex-col justify-between p-4 border-r border-white/20 text-xs z-10 text-cyan-400">
    
    
    {/* Center Data Points (Writing Vertical) - 6-word marketing focus */}
    <div className="flex-1 flex flex-col justify-center items-center font-mono opacity-80">
        {/* Adjusted vertical text size to fit in smaller mobile space */}
        <div className="writing-vertical-rl tracking-widest text-[10px] md:text-base"> 
            <span className="text-white/80">DATA SCIENCE | SOFTWARE ENG |NIXQ...</span>
        </div>
    </div>

    {/* Bottom Version/Build Info - Expanded content */}
    <div className="opacity-60 text-right font-mono text-[8px] md:text-[10px] space-y-1">
        <div>BUILD: 24.Q4.R1-STABLE</div>
        <div>ENV: PRODUCTION</div>
        <div>COMMIT: 8a7c9f3</div>
        <div className="text-gray-500">INIT_TIME: 16:20:05Z</div>
        <div>ENCRYPT: AES-256</div>
    </div>
</div>

            {/* 2. Fixed Vertical Data Bars (System Status) - RIGHT SIDEBAR */}
            {/* FIX: Changed w-24 to w-12 on mobile, w-24 on md screens and up */}
            <div className="fixed top-0 bottom-0 right-0 w-12 md:w-24 flex flex-col justify-between p-4 border-l border-white/20 text-xs z-10">
                <div className="opacity-70 text-right">
                </div>
                {/* Adjusted vertical text size to fit in smaller mobile space */}
                <div className="writing-vertical-rl transform rotate-180 opacity-70 text-[10px] md:text-xs">
                    <span className="tracking-widest">QUERY | ACCESS | DATABASE | DECRYPT</span>
                </div>
            </div>

            {/* 3. Fixed Header */}
            {/* FIX: left-24 right-24 changed to left-12 right-12 on mobile, md:left-24 md:right-24 on desktop */}
            <header className="fixed top-0 left-12 right-12 md:left-24 md:right-24 h-12 flex items-center justify-between px-4 border-b border-white/20 z-10 text-xs">
                <div className="flex space-x-4">
                    {/* Static placeholder for left side */}
                </div>
                {/* Static status message */}
                {/* <span className="text-white tracking-widest uppercase">
                    INTERFACE STATUS: ACTIVE
                </span> */}
            </header>

            {/* 4. Main Monolith Content Area (The Hero Text) */}
            {/* FIX: mx-24 changed to mx-12 on mobile, md:mx-24 on desktop */}
            <main className="relative z-10 min-h-screen pt-12 pb-12 mx-12 md:mx-24 flex flex-col items-center justify-center">
                
                {/* A. Dynamic Hacking/Cipher Block */}
                <div className="text-center mb-16 max-w-4xl px-4">
                    <p className="text-xs text-red-500 uppercase tracking-widest mb-4">
                        <CipherText text="Virtually limitless" delay={50} />
                    </p>
                    
                    <h1 className="text-6xl md:text-8xl lg:text-[10vw] font-black leading-none uppercase tracking-tight mix-blend-difference">
                        <span className="text-white/90">
                           <CipherText text="NIXQUE" delay={500} />
                        </span>
                        <br />
                        <span className="text-white/70">
                           <CipherText text="Ltd" delay={1000} />
                        </span>
                    </h1>

                    <p className="mt-8 text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed">
                        <CipherText text="DEVELOPING YOUR BEST DIGITAL PRODUCTS. 
                        KEEN TO TRANSFORM IDEAS INTO EXCEPTIONAL DIGITAL EXPERIENCES.
                    
                        " delay={1500} />
                    </p>
                </div>

            {/* B. Status Display Terminal (Mock UI) - Marketing Edition */}
<div className="w-full max-w-xl border border-white/30 p-4 mt-8 bg-black/50 backdrop-blur-sm shadow-[0_0_15px_rgba(255,255,255,0.1)]">
    <div className="text-xs space-y-2 font-mono">
        <p>
            <span className="text-cyan-400">[INIT]</span> <i>Nixque Kernel v2.1</i> loading <i>Computational Integrity Matrix</i>...
        </p>
        <p>
            <span className="text-green-400">[READY]</span> <i>Quantum Data Pipeline</i> status: <span className="text-yellow-400">ONLINE</span>
        </p>
        <p>
            <span className="text-green-400">[INFO]</span> Executing <i>Biostatistical Model</i> run <span className="text-white/80">ID: NQX-472-A</span>... 
            <span className="float-right text-green-400">100%</span>
        </p>
        <p className="text-gray-500">
            <span className="text-purple-400">[SECURITY]</span> <i>Threat Vector Analysis</i> complete. <span className="text-gray-300">No anomalies detected.</span>
        </p>
        <p className="text-gray-500">
            <span className="text-yellow-400">[PROCESS]</span> Committing <i>Digital Product</i> schema changes to Gitlab... 
            <span className="inline-block w-2 h-2 bg-yellow-400 rounded-full animate-pulse ml-2"></span>
        </p>
        <div className="h-4" />
        <p className="text-white/80">
            $ <i>NIXQUE_LAB</i> > <i>Deploying Innovation.</i>
            <span className="text-white/90 underline ml-1">_</span>
        </p>
    </div>
</div>

            </main>

            {/* Styles for Vertical Text */}
            <style jsx global>{`
                .writing-vertical-rl {
                    writing-mode: vertical-rl;
                    text-orientation: mixed;
                    transform: rotate(180deg);
                }
            `}</style>
        </div>
    );
};

export default BrutalistMonolithInterface;